import { useQuery } from '@tanstack/react-query';
import { getInboxFunnels } from 'API/linkedin-funnel';
import { AxiosError } from 'axios';
import { useDebounce } from 'hooks/use-debounce';
import { useUserData } from 'hooks/use-user-data';
import { useEffect } from 'react';
import useErrorStore, { CustomErrorClass } from 'stores/error-store';
import { InboxCampaign } from './funnel-inbox.types';

interface CampaignsResponse {
  funnels: InboxCampaign[];
  totalFunnels: number;
  totalUnreadMessages: number;
}

const FETCH_INTERVAL = 15 * 60 * 1000;
const DEBOUNCE_DELAY = 500;

export const useInboxCampaigns = (
  type: string | undefined,
  liAccountsInitiated: boolean,
  searchParam: string,
  searchFilters: string[],
  selectedLinkedinAccounts: string[],
  resetCampaignSelection: () => void
) => {
  const { token } = useUserData();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const debouncedSearchParam = useDebounce(searchParam, DEBOUNCE_DELAY);

  const { data, isLoading, isError, error } = useQuery<CampaignsResponse>({
    queryKey: ['inboxCampaigns', token, type, debouncedSearchParam, searchFilters, selectedLinkedinAccounts],
    queryFn: ({ signal }) => getInboxFunnels(type, debouncedSearchParam, searchFilters, selectedLinkedinAccounts, signal),
    // Ohne ausgewähltes Konto nichts laden: die Anfrage ginge sonst ohne Kontofilter hinaus,
    // und das Backend fiele auf die Konten des Nutzers zurück.
    enabled: !!type && liAccountsInitiated && selectedLinkedinAccounts.length > 0,
    refetchInterval: FETCH_INTERVAL,
  });
  const noneSelected = liAccountsInitiated && selectedLinkedinAccounts.length === 0;

  useEffect(() => {
    if (isError) {
      const customError = new CustomErrorClass(error as AxiosError);
      handleOpenErrorDialog(customError, customError.request, customError.requestType);
    }
  }, [isError, error]);

  useEffect(() => {
    resetCampaignSelection();
  }, [debouncedSearchParam, searchFilters, selectedLinkedinAccounts]);

  if (noneSelected) {
    return { campaigns: [], totalCampaigns: 0, totalUnreadMessages: 0, isLoading: false };
  }

  return {
    campaigns: Array.isArray(data?.funnels) ? data.funnels : [],
    totalCampaigns: data?.totalFunnels || 0,
    totalUnreadMessages: data?.totalUnreadMessages || 0,
    isLoading,
  };
};
